import React, { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import DataTable from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';
import FormField from '../../components/common/FormField';
import { Loader, ErrorBanner, SuccessBanner } from '../../components/common/Feedback';
import { useAuth } from '../../context/AuthContext';
import * as documentsApi from '../../api/documents';
import * as clientsApi from '../../api/clients';
import { formatDate } from '../../utils/format';

const CATEGORY_OPTIONS = ['Audit Documents', 'Tax Documents', 'Financial Statements', 'Bank Statements', 'Reports', 'Supporting Documents'].map((v) => ({ value: v, label: v }));

export default function Documents() {
  const { actor } = useAuth();
  const canDelete = actor.role === 'super_admin' || actor.role === 'admin';
  const isClient = actor.role === 'client';

  const [rows, setRows] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [clientOptions, setClientOptions] = useState([]);
  const [uploadClientId, setUploadClientId] = useState(isClient ? actor.profile.id : '');
  const [uploadCategory, setUploadCategory] = useState('Supporting Documents');
  const [uploadFile, setUploadFile] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    documentsApi
      .listDocuments({ category: categoryFilter || undefined, limit: 200 })
      .then((res) => setRows(res.data))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load documents.'))
      .finally(() => setLoading(false));
  }, [categoryFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const openUpload = async () => {
    setUploadFile(null);
    setUploadCategory('Supporting Documents');
    if (!isClient && clientOptions.length === 0) {
      const res = await clientsApi.listClients({ limit: 500 });
      setClientOptions(res.data.map((c) => ({ value: c.id, label: c.clientName })));
    }
    setShowForm(true);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      setError('Please choose a file to upload.');
      return;
    }
    try {
      await documentsApi.uploadDocument({ file: uploadFile, clientId: uploadClientId, category: uploadCategory });
      setSuccess('Document uploaded.');
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed.');
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete "${row.fileName}"?`)) return;
    try {
      await documentsApi.deleteDocument(row.id);
      setSuccess('Document deleted.');
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete document.');
    }
  };

  const columns = [
    { key: 'fileName', label: 'File Name' },
    { key: 'category', label: 'Category' },
    { key: 'client', label: 'Client', render: (r) => r.client?.clientName || '-' },
    { key: 'version', label: 'Version', render: (r) => `v${r.version}` },
    { key: 'createdAt', label: 'Uploaded', render: (r) => formatDate(r.createdAt) },
    {
      key: 'actions',
      label: 'Actions',
      render: (r) => (
        <div className="btn-group">
          <button type="button" className="btn-link" onClick={() => documentsApi.downloadDocument(r.id, r.fileName)}>Download</button>
          {canDelete && <button type="button" className="btn-link" onClick={() => handleDelete(r)}>Delete</button>}
        </div>
      ),
    },
  ];

  return (
    <DashboardLayout title="Documents">
      <div className="page-header">
        <div>
          <h1 className="heading">Document Library</h1>
          <p className="page-subtext">All files are versioned automatically when re-uploaded with the same name.</p>
        </div>
        <button type="button" className="btn" onClick={openUpload}>Upload Document</button>
      </div>

      <ErrorBanner message={error} />
      <SuccessBanner message={success} />

      <div className="filter-bar">
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
          <option value="">All Categories</option>
          {CATEGORY_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </div>

      {loading ? <Loader text="Loading documents..." /> : <DataTable columns={columns} rows={rows} getRowKey={(r) => r.id} emptyMessage="No documents found." />}

      {showForm && (
        <Modal
          title="Upload Document"
          onClose={() => setShowForm(false)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" form="upload-form" className="btn">Upload</button>
            </>
          }
        >
          <form id="upload-form" onSubmit={handleUpload}>
            <div className="form-grid single-column">
              {!isClient && (
                <FormField label="Client" type="select" options={clientOptions} value={uploadClientId} onChange={(e) => setUploadClientId(e.target.value)} required />
              )}
              <FormField label="Category" type="select" options={CATEGORY_OPTIONS} value={uploadCategory} onChange={(e) => setUploadCategory(e.target.value)} />
              <FormField label="File" type="file" onChange={(e) => setUploadFile(e.target.files[0])} required />
            </div>
          </form>
        </Modal>
      )}
    </DashboardLayout>
  );
}